import { useState } from 'react';
import { useForgeNavigate } from '../components/layout/ForgeTransition';
import { Navbar } from '../components/layout/Navbar';
import { PageWrapper } from '../components/layout/PageWrapper';
import { ProgressTracker } from '../components/layout/ProgressTracker';
import { Card } from '../components/shared/Card';
import { Button } from '../components/shared/Button';
import { useAppState } from '../hooks/useAppState';
import { BookOpen, BarChart3, Trophy } from 'lucide-react';

type Experience = 'new' | 'backtesting' | 'proven' | null;

export function Assessment() {
  const forgeNavigate = useForgeNavigate();
  const { updateUser } = useAppState();
  const [experience, setExperience] = useState<Experience>(null);

  const options = [
    {
      value: 'new' as const,
      icon: BookOpen,
      title: 'New to Trading',
      desc: 'Less than 6 months experience. I need to master a model first.',
      route: '/learn',
    },
    {
      value: 'backtesting' as const,
      icon: BarChart3,
      title: 'Building My Track Record',
      desc: '6-12 months. I\'ve been backtesting and want my protocol.',
      route: '/stats/backtest',
    },
    {
      value: 'proven' as const,
      icon: Trophy,
      title: 'Proven Live Trader',
      desc: '12+ months. I have live prop firm trading history.',
      route: '/stats/proven',
    },
  ];

  function handleContinue() {
    if (!experience) return;
    const selected = options.find(o => o.value === experience)!;
    updateUser({ traderStage: experience });
    forgeNavigate(selected.route, 2);
  }

  return (
    <PageWrapper warmth={0}>
      <Navbar />
      <main className="min-h-screen pt-16">
        <ProgressTracker currentStep={2} />

        <div className="max-w-[660px] mx-auto px-8 pb-16">
          <h1 className="text-2xl md:text-3xl font-bold font-mono tracking-wide text-shimmer text-center mb-3">Where are you on your journey?</h1>
          <p className="text-[13px] font-mono text-white/35 text-center mb-10 tracking-wide">
            This helps us tailor your experience and protocol.
          </p>

          <div className="flex flex-col gap-4">
            {options.map((opt, idx) => {
              const Icon = opt.icon;
              return (
                <Card
                  key={opt.value}
                  selected={experience === opt.value}
                  onClick={() => setExperience(opt.value)}
                  bento
                  className="animate-card-enter"
                  style={{ animationDelay: `${idx * 120}ms` }}
                >
                  <div className="flex items-start gap-5">
                    <div className={`
                      w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300
                      ${experience === opt.value ? 'bg-white/[0.08] border border-white/[0.15]' : 'bg-white/[0.03] border border-white/[0.04]'}
                    `}>
                      <Icon className={`w-6 h-6 transition-colors duration-300 ${experience === opt.value ? 'text-white/80' : 'text-white/30'}`} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold font-mono text-white/90 mb-1.5">{opt.title}</h3>
                      <p className="text-sm font-mono text-white/35 leading-relaxed">{opt.desc}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="mt-10 flex justify-center">
            <Button size="lg" onClick={handleContinue} disabled={!experience}>
              Continue
            </Button>
          </div>
        </div>
      </main>
    </PageWrapper>
  );
}
